// Ingredient suggestion: an item-to-item model learned from the ingredients used together in past
// perfumes, with styles (groups of similar perfumes) and the usual volume of each category.
// See docs/ingredient-suggestion.md
//
// The files of this folder, except index.ts, are also run by plain Node (admin-scripts/
// trainSuggestionModel.js, Node type stripping): only top-level `import type` from "@/…",
// relative imports with their .ts extension of files without other imports, no enum, no JSON
// import.
import type { LocalizedText, PerfumeIngredient } from "@/models";
import { PERFUME_BOTTLE_VOLUME, bottleVolumeLeft } from "../../models/PerfumeBottle.ts";

export const SUGGESTION_MODEL_VERSION = 3;
export const MAX_SUGGESTIONS = 10;
export const DEFAULT_SUGGESTED_VOLUME = 5;
export const DEFAULT_ALPHA = 5;
// a perfume is used for training when it has at least this number of distinct ingredients
export const DEFAULT_MIN_COMPONENTS = 3;
// pairs found in fewer perfumes are dropped: no single formula can be rebuilt from the model
export const DEFAULT_MIN_PAIR_COUNT = 2;
// a finished workshop perfume is about 101 ml: the others (tests, unfinished or odd formulas) are
// left out of the training
export const DEFAULT_TARGET_VOLUME = 101;
export const DEFAULT_VOLUME_TOLERANCE = 10;
// "Goes well with X": X and the suggestion were used together in at least this number of perfumes
const PAIRED_WITH_MIN_COUNT = 3;
const PAIRED_WITH_MAX = 2;
// "Found in X % of our perfumes": only with enough perfumes, and for frequent ingredients
const POPULAR_MIN_PERFUMES = 20;
const POPULAR_MIN_PERCENT = 10;
// "In X % of the perfumes of this style": frequent in the style and more than elsewhere
const STYLE_REASON_MIN_PERCENT = 25;
const STYLE_REASON_MIN_LIFT = 1.5;
// number of ingredients kept to describe a style
const STYLE_SIGNATURE_SIZE = 4;

// Firestore names of ComponentCategory, in the order of the perfume tunnel
export const CATEGORY_NAMES = ["NOTE_DE_TETE", "NOTE_DE_COEUR", "NOTE_DE_FOND"] as const;
export type CategoryName = (typeof CATEGORY_NAMES)[number];

// How the ingredients of the perfume are combined to score a candidate j, with P(j | i) the
// smoothed probability that a perfume containing i also contains j, and p_j the share of perfumes
// containing j:
// - mean: average of P(j | i)
// - naiveBayes: log p_j + w · sum of log(P(j | i) / p_j): each ingredient raises or lowers the
//   odds; w (evidenceWeight) below 1 makes up for the ingredients of a perfume not being independent
// - cosine: sum of n_ij / sqrt(n_i · n_j)
// The training script evaluates them and keeps the best one in the model.
// A style chosen by the client adds styleWeight · log(P(j | style) / p_j).
export const SCORING_METHODS = ["mean", "naiveBayes", "cosine"] as const;
export type ScoringMethod = (typeof SCORING_METHODS)[number];
// best on the production perfumes (2026-10-11), see docs/ingredient-suggestion.md
export const DEFAULT_SCORING_METHOD: ScoringMethod = "naiveBayes";
export const DEFAULT_EVIDENCE_WEIGHT = 0.5;
// a style chip changes about half of the suggestions (docs/ingredient-suggestion.md)
export const DEFAULT_STYLE_WEIGHT = 2;

export interface Scorer {
  method: ScoringMethod;
  alpha: number;
  // naiveBayes only
  evidenceWeight: number;
  // weight of the style chosen by the client
  styleWeight: number;
}

export interface SuggestionModelIngredient {
  name: string;
  category: string;
  // number of perfumes containing this ingredient
  count: number;
  // median volume (ml) of this ingredient in these perfumes
  medianVolume: number;
}

export interface SuggestionModelCategory {
  name: string;
  // median total volume (ml) and number of distinct ingredients of this category in a perfume
  medianVolume: number;
  medianCount: number;
}

export interface SuggestionModelStyle {
  // the most distinctive ingredient of the style
  id: string;
  // given in admin-scripts/suggestionStyleNames.json; without it the signature is shown
  label: LocalizedText | null;
  perfumeCount: number;
  // the most distinctive ingredients, the first one is the id
  signature: string[];
  // counts[i]: number of perfumes of this style containing ingredients[i]
  counts: number[];
}

export interface SuggestionModel {
  version: typeof SUGGESTION_MODEL_VERSION;
  trainedOn: string;
  perfumeCount: number;
  method: ScoringMethod;
  alpha: number;
  evidenceWeight: number;
  styleWeight: number;
  minPairCount: number;
  categories: SuggestionModelCategory[];
  // sorted by name
  ingredients: SuggestionModelIngredient[];
  // pairCounts[i][k]: number of perfumes containing both ingredients[i] and ingredients[i + 1 + k]
  pairCounts: number[][];
  styles: SuggestionModelStyle[];
}

// a perfume as stored in Firestore: read defensively
export interface TrainingPerfume {
  components?: readonly {
    name?: unknown;
    volume?: unknown;
    category?: unknown;
    suggested?: unknown;
  }[];
}

export interface SuggestionStyle {
  id: string;
  label: LocalizedText | null;
  perfumeCount: number;
  signature: string[];
  counts: Float64Array;
}

export interface SuggestionIndex {
  perfumeCount: number;
  method: ScoringMethod;
  alpha: number;
  evidenceWeight: number;
  styleWeight: number;
  ingredients: Map<string, SuggestionModelIngredient>;
  // ingredient name → position in names, counts and pairCounts
  ids: Map<string, number>;
  names: string[];
  counts: Float64Array;
  // symmetric matrix, pairCounts[a * size + b]
  pairCounts: Float64Array;
  size: number;
  categoryMedianVolumes: Map<string, number>;
  categories: Map<string, SuggestionModelCategory>;
  styles: SuggestionStyle[];
}

// what the perfume already contains in a category
export interface CategoryUsage {
  volume: number;
  count: number;
}

export type SuggestionReason =
  | { kind: "pairedWith"; ingredients: PerfumeIngredient[] }
  | { kind: "style"; percent: number }
  | { kind: "popular"; percent: number };

export interface IngredientSuggestion {
  ingredient: PerfumeIngredient;
  volume: number;
  reason: SuggestionReason | null;
}

export function normalizeIngredientName(name: string): string {
  return name.trim().replace(/\s+/g, " ").toUpperCase();
}

export function emptySuggestionModel(): SuggestionModel {
  return {
    version: SUGGESTION_MODEL_VERSION,
    trainedOn: "",
    perfumeCount: 0,
    method: DEFAULT_SCORING_METHOD,
    alpha: DEFAULT_ALPHA,
    evidenceWeight: DEFAULT_EVIDENCE_WEIGHT,
    styleWeight: DEFAULT_STYLE_WEIGHT,
    minPairCount: DEFAULT_MIN_PAIR_COUNT,
    categories: [],
    ingredients: [],
    pairCounts: [],
    styles: [],
  };
}

// ---------------------------------------------------------------------------------------------
// Training

export interface PerfumeIngredientUse {
  volume: number;
  category: string;
}

// distinct ingredients of a perfume; the lots of a same ingredient are added up
export function perfumeIngredientUses(perfume: TrainingPerfume): Map<string, PerfumeIngredientUse> {
  const uses = new Map<string, PerfumeIngredientUse>();
  for (const component of perfume.components ?? []) {
    if (typeof component?.name !== "string") continue;
    const name = normalizeIngredientName(component.name);
    const volume = Number(component.volume);
    if (name === "" || !Number.isFinite(volume) || volume <= 0) continue;
    const category = typeof component.category === "string" ? component.category : "";
    const use = uses.get(name);
    if (use) {
      use.volume += volume;
    } else {
      uses.set(name, { volume, category });
    }
  }
  return uses;
}

// total volume (ml) of a perfume, empty components left out
export function perfumeTotalVolume(perfume: TrainingPerfume): number {
  let total = 0;
  for (const use of perfumeIngredientUses(perfume).values()) total += use.volume;
  return total;
}

// splits the perfumes by total volume: kept within targetVolume ± tolerance ml
export function selectPerfumesByVolume<T extends TrainingPerfume>(
  perfumes: readonly T[],
  {
    targetVolume = DEFAULT_TARGET_VOLUME,
    tolerance = DEFAULT_VOLUME_TOLERANCE,
  }: { targetVolume?: number; tolerance?: number } = {},
): { kept: T[]; tooSmall: T[]; tooLarge: T[] } {
  const selection: { kept: T[]; tooSmall: T[]; tooLarge: T[] } = {
    kept: [],
    tooSmall: [],
    tooLarge: [],
  };
  for (const perfume of perfumes) {
    const volume = perfumeTotalVolume(perfume);
    if (volume < targetVolume - tolerance) {
      selection.tooSmall.push(perfume);
    } else if (volume > targetVolume + tolerance) {
      selection.tooLarge.push(perfume);
    } else {
      selection.kept.push(perfume);
    }
  }
  return selection;
}

export function categoryUsage(
  uses: ReadonlyMap<string, PerfumeIngredientUse>,
  category: string,
): CategoryUsage {
  const usage = { volume: 0, count: 0 };
  for (const use of uses.values()) {
    if (use.category !== category) continue;
    usage.volume += use.volume;
    usage.count++;
  }
  return usage;
}

export function median(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

function byName(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

// the ingredients most typical of a style: frequent in it, and more frequent than elsewhere
function styleSignature(
  names: readonly string[],
  styleCounts: ArrayLike<number>,
  stylePerfumeCount: number,
  counts: ArrayLike<number>,
  perfumeCount: number,
): string[] {
  return names
    .map((name, i) => {
      const inStyle = styleCounts[i] / stylePerfumeCount;
      const overall = counts[i] / perfumeCount;
      return {
        name,
        inStyle,
        weight: inStyle > overall ? inStyle * Math.log(inStyle / overall) : 0,
      };
    })
    .filter((s) => s.weight > 0)
    .sort((a, b) => b.weight - a.weight || byName(a.name, b.name))
    .slice(0, STYLE_SIGNATURE_SIZE)
    .map((s) => s.name);
}

export function buildSuggestionModel(
  perfumes: readonly TrainingPerfume[],
  {
    trainedOn,
    method = DEFAULT_SCORING_METHOD,
    alpha = DEFAULT_ALPHA,
    evidenceWeight = DEFAULT_EVIDENCE_WEIGHT,
    styleWeight = DEFAULT_STYLE_WEIGHT,
    minComponents = DEFAULT_MIN_COMPONENTS,
    minPairCount = DEFAULT_MIN_PAIR_COUNT,
    styles,
  }: {
    trainedOn: string;
    method?: ScoringMethod;
    alpha?: number;
    evidenceWeight?: number;
    styleWeight?: number;
    minComponents?: number;
    minPairCount?: number;
    // style of each perfume of `perfumes` (-1: none), see learnStyles in suggestionStyles.ts
    styles?: {
      count: number;
      assignments: readonly number[];
      labels?: Readonly<Record<string, LocalizedText>>;
    };
  },
): SuggestionModel {
  const eligible: { uses: Map<string, PerfumeIngredientUse>; style: number }[] = [];
  perfumes.forEach((perfume, p) => {
    const uses = perfumeIngredientUses(perfume);
    if (uses.size >= minComponents) eligible.push({ uses, style: styles?.assignments[p] ?? -1 });
  });

  const ingredientStats = new Map<
    string,
    { count: number; volumes: number[]; categories: Map<string, number> }
  >();
  for (const { uses } of eligible) {
    for (const [name, use] of uses) {
      let stats = ingredientStats.get(name);
      if (!stats) {
        stats = { count: 0, volumes: [], categories: new Map() };
        ingredientStats.set(name, stats);
      }
      stats.count++;
      stats.volumes.push(use.volume);
      stats.categories.set(use.category, (stats.categories.get(use.category) ?? 0) + 1);
    }
  }

  const names = [...ingredientStats.keys()].sort(byName);
  const ids = new Map(names.map((name, id) => [name, id]));
  const size = names.length;
  const pairs = new Float64Array(size * size);
  const styleCount = styles?.count ?? 0;
  const styleCounts = Array.from({ length: styleCount }, () => new Float64Array(size));
  const stylePerfumeCounts = Array.from({ length: styleCount }, () => 0);
  for (const { uses, style } of eligible) {
    const perfumeIds = [...uses.keys()].map((name) => ids.get(name)!).sort((a, b) => a - b);
    for (let i = 0; i < perfumeIds.length; i++) {
      for (let j = i + 1; j < perfumeIds.length; j++) {
        pairs[perfumeIds[i] * size + perfumeIds[j]]++;
      }
    }
    if (style >= 0 && style < styleCount) {
      stylePerfumeCounts[style]++;
      for (const id of perfumeIds) styleCounts[style][id]++;
    }
  }

  const ingredients = names.map((name) => {
    const stats = ingredientStats.get(name)!;
    return {
      name,
      // the category used most often, the first one in name order on a tie
      category: [...stats.categories.entries()].sort(
        ([ca, na], [cb, nb]) => nb - na || byName(ca, cb),
      )[0][0],
      count: stats.count,
      medianVolume: Math.round(median(stats.volumes)),
    };
  });
  const pairCounts = names.map((_, a) =>
    names.slice(a + 1).map((_, k) => {
      const count = pairs[a * size + a + 1 + k];
      return count >= minPairCount ? count : 0;
    }),
  );

  const categories = eligible.length
    ? CATEGORY_NAMES.map((name) => {
        const usages = eligible.map(({ uses }) => categoryUsage(uses, name));
        return {
          name,
          medianVolume: Math.round(median(usages.map((u) => u.volume))),
          medianCount: Math.round(median(usages.map((u) => u.count))),
        };
      })
    : [];

  const counts = ingredients.map((i) => i.count);
  const usedIds = new Set<string>();
  const modelStyles = styleCounts
    .map((countsInStyle, s) => ({ countsInStyle, perfumeCount: stylePerfumeCounts[s] }))
    .filter(({ perfumeCount }) => perfumeCount > 0)
    .map(({ countsInStyle, perfumeCount }, s) => {
      const signature = styleSignature(names, countsInStyle, perfumeCount, counts, eligible.length);
      let id = signature[0] ?? `STYLE ${s + 1}`;
      while (usedIds.has(id)) id = `${id}+`;
      usedIds.add(id);
      return {
        id,
        label: styles?.labels?.[id] ?? null,
        perfumeCount,
        signature,
        counts: Array.from(countsInStyle),
      };
    });

  return {
    version: SUGGESTION_MODEL_VERSION,
    trainedOn,
    perfumeCount: eligible.length,
    method,
    alpha,
    evidenceWeight,
    styleWeight,
    minPairCount,
    categories,
    ingredients,
    pairCounts,
    styles: modelStyles,
  };
}

// the pairs of the model found in at least one perfume: [a, b, number of perfumes], a < b
export function listModelPairs(model: SuggestionModel): [string, string, number][] {
  return model.pairCounts.flatMap((row, a) =>
    row.flatMap((count, k): [string, string, number][] =>
      count > 0 ? [[model.ingredients[a].name, model.ingredients[a + 1 + k].name, count]] : [],
    ),
  );
}

// one ingredient, row of pair counts or style per line
export function serializeSuggestionModel(model: SuggestionModel): string {
  const list = (items: readonly unknown[]) =>
    items.length === 0
      ? "[]"
      : `[\n${items.map((item) => `    ${JSON.stringify(item)}`).join(",\n")}\n  ]`;
  return [
    "{",
    `  "version": ${JSON.stringify(model.version)},`,
    `  "trainedOn": ${JSON.stringify(model.trainedOn)},`,
    `  "perfumeCount": ${JSON.stringify(model.perfumeCount)},`,
    `  "method": ${JSON.stringify(model.method)},`,
    `  "alpha": ${JSON.stringify(model.alpha)},`,
    `  "evidenceWeight": ${JSON.stringify(model.evidenceWeight)},`,
    `  "styleWeight": ${JSON.stringify(model.styleWeight)},`,
    `  "minPairCount": ${JSON.stringify(model.minPairCount)},`,
    `  "categories": ${list(model.categories)},`,
    `  "ingredients": ${list(model.ingredients)},`,
    `  "pairCounts": ${list(model.pairCounts)},`,
    `  "styles": ${list(model.styles)}`,
    "}",
    "",
  ].join("\n");
}

function isCount(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}

function isModelIngredient(value: unknown): value is SuggestionModelIngredient {
  const ingredient = value as SuggestionModelIngredient;
  return (
    typeof ingredient === "object" &&
    ingredient !== null &&
    typeof ingredient.name === "string" &&
    typeof ingredient.category === "string" &&
    isCount(ingredient.count) &&
    isCount(ingredient.medianVolume)
  );
}

function isModelCategory(value: unknown): value is SuggestionModelCategory {
  const category = value as SuggestionModelCategory;
  return (
    typeof category === "object" &&
    category !== null &&
    typeof category.name === "string" &&
    isCount(category.medianVolume) &&
    isCount(category.medianCount)
  );
}

function isLabel(value: unknown): value is LocalizedText | null {
  const label = value as LocalizedText | null;
  return (
    label === null ||
    (typeof label === "object" && typeof label.fr === "string" && typeof label.en === "string")
  );
}

function isModelStyle(value: unknown, ingredientCount: number): value is SuggestionModelStyle {
  const style = value as SuggestionModelStyle;
  return (
    typeof style === "object" &&
    style !== null &&
    typeof style.id === "string" &&
    isLabel(style.label) &&
    isCount(style.perfumeCount) &&
    Array.isArray(style.signature) &&
    style.signature.every((name) => typeof name === "string") &&
    Array.isArray(style.counts) &&
    style.counts.length === ingredientCount &&
    style.counts.every(isCount)
  );
}

// an invalid model (or a model of another version) gives the empty model: suggestions still work,
// by popularity order then at random
export function parseSuggestionModel(raw: unknown): SuggestionModel {
  const model = raw as SuggestionModel;
  if (
    typeof model !== "object" ||
    model === null ||
    model.version !== SUGGESTION_MODEL_VERSION ||
    typeof model.trainedOn !== "string" ||
    !isCount(model.perfumeCount) ||
    !SCORING_METHODS.includes(model.method) ||
    !isCount(model.alpha) ||
    !isCount(model.evidenceWeight) ||
    !isCount(model.styleWeight) ||
    !isCount(model.minPairCount) ||
    !Array.isArray(model.categories) ||
    !model.categories.every(isModelCategory) ||
    !Array.isArray(model.ingredients) ||
    !model.ingredients.every(isModelIngredient) ||
    !Array.isArray(model.pairCounts) ||
    model.pairCounts.length !== model.ingredients.length ||
    !model.pairCounts.every(
      (row, a) =>
        Array.isArray(row) && row.length === model.ingredients.length - 1 - a && row.every(isCount),
    ) ||
    !Array.isArray(model.styles) ||
    !model.styles.every((style) => isModelStyle(style, model.ingredients.length))
  ) {
    return emptySuggestionModel();
  }
  return model;
}

// ---------------------------------------------------------------------------------------------
// Scoring

export function indexSuggestionModel(model: SuggestionModel): SuggestionIndex {
  const size = model.ingredients.length;
  const ingredients = new Map(model.ingredients.map((i) => [normalizeIngredientName(i.name), i]));
  const names = model.ingredients.map((i) => normalizeIngredientName(i.name));
  const ids = new Map(names.map((name, id) => [name, id]));
  const counts = Float64Array.from(model.ingredients, (i) => i.count);
  const pairCounts = new Float64Array(size * size);
  model.pairCounts.forEach((row, a) =>
    row.forEach((count, k) => {
      const b = a + 1 + k;
      pairCounts[a * size + b] = count;
      pairCounts[b * size + a] = count;
    }),
  );
  const volumesByCategory = new Map<string, number[]>();
  for (const ingredient of model.ingredients) {
    if (ingredient.count === 0 || ingredient.medianVolume <= 0) continue;
    const volumes = volumesByCategory.get(ingredient.category) ?? [];
    volumes.push(ingredient.medianVolume);
    volumesByCategory.set(ingredient.category, volumes);
  }
  const categoryMedianVolumes = new Map(
    [...volumesByCategory.entries()].map(([category, volumes]) => [category, median(volumes)]),
  );
  return {
    perfumeCount: model.perfumeCount,
    method: model.method,
    alpha: model.alpha,
    evidenceWeight: model.evidenceWeight,
    styleWeight: model.styleWeight,
    ingredients,
    ids,
    names,
    counts,
    pairCounts,
    size,
    categoryMedianVolumes,
    categories: new Map(model.categories.map((c) => [c.name, c])),
    styles: model.styles.map((style) => ({
      id: style.id,
      label: style.label,
      perfumeCount: style.perfumeCount,
      signature: style.signature,
      counts: Float64Array.from(style.counts),
    })),
  };
}

// position of an ingredient in the index, -1 when unknown to the model
function idOf(index: SuggestionIndex, name: string): number {
  return index.ids.get(normalizeIngredientName(name)) ?? -1;
}

function countOf(index: SuggestionIndex, id: number): number {
  return id === -1 ? 0 : index.counts[id];
}

function pairCountOf(index: SuggestionIndex, a: number, b: number): number {
  return a === -1 || b === -1 ? 0 : index.pairCounts[a * index.size + b];
}

// share of perfumes containing `id`, smoothed: never 0, even for an ingredient unknown to the model
function prior(index: SuggestionIndex, id: number): number {
  return (countOf(index, id) + 1) / (index.perfumeCount + 2);
}

// probability that a perfume containing `present` also contains `candidate`, smoothed toward the
// prior of `candidate` when `present` is rare
function conditional(
  index: SuggestionIndex,
  candidate: number,
  present: number,
  alpha: number,
): number {
  return (
    (pairCountOf(index, candidate, present) + alpha * prior(index, candidate)) /
    (countOf(index, present) + alpha)
  );
}

// probability that a perfume of `style` contains `id`, smoothed toward its prior
function inStyle(
  index: SuggestionIndex,
  style: SuggestionStyle,
  id: number,
  alpha: number,
): number {
  return (
    ((id === -1 ? 0 : style.counts[id]) + alpha * prior(index, id)) / (style.perfumeCount + alpha)
  );
}

// ingredients of the perfume known to the model, without duplicates
function knownContext(index: SuggestionIndex, present: readonly string[]): number[] {
  return [...new Set(present.map((name) => idOf(index, name)))].filter(
    (id) => id !== -1 && countOf(index, id) > 0,
  );
}

function score(
  index: SuggestionIndex,
  candidate: number,
  context: readonly number[],
  { method, alpha, evidenceWeight, styleWeight }: Scorer,
  style: SuggestionStyle | null,
): number {
  const candidatePrior = prior(index, candidate);
  if (context.length === 0 && style === null) return candidatePrior;
  const styleEvidence =
    style === null
      ? 0
      : styleWeight * Math.log(inStyle(index, style, candidate, alpha) / candidatePrior);
  if (context.length === 0) return Math.log(candidatePrior) + styleEvidence;
  let sum = 0;
  switch (method) {
    case "mean":
      for (const i of context) sum += conditional(index, candidate, i, alpha);
      return Math.log(sum / context.length) + styleEvidence;
    case "naiveBayes":
      for (const i of context)
        sum += Math.log(conditional(index, candidate, i, alpha) / candidatePrior);
      return Math.log(candidatePrior) + evidenceWeight * sum + styleEvidence;
    case "cosine": {
      const candidateCount = countOf(index, candidate) + alpha;
      for (const i of context) {
        sum +=
          pairCountOf(index, candidate, i) /
          Math.sqrt(candidateCount * (countOf(index, i) + alpha));
      }
      // ingredients never used with the perfume ones: by popularity
      return Math.log(sum + candidatePrior * 1e-6) + styleEvidence;
    }
  }
}

function findStyle(index: SuggestionIndex, styleId: string | null | undefined) {
  return styleId ? (index.styles.find((s) => s.id === styleId) ?? null) : null;
}

// candidates sorted by decreasing score; on a tie, the order of `candidates` is kept
export function scoreCandidates(
  index: SuggestionIndex,
  present: readonly string[],
  candidates: readonly string[],
  {
    method = index.method,
    alpha = index.alpha,
    evidenceWeight = index.evidenceWeight,
    styleWeight = index.styleWeight,
    style: styleId = null,
  }: Partial<Scorer> & { style?: string | null } = {},
): { name: string; score: number }[] {
  const context = knownContext(index, present);
  const scorer = { method, alpha, evidenceWeight, styleWeight };
  const style = findStyle(index, styleId);
  return candidates
    .map((name) => ({ name, score: score(index, idOf(index, name), context, scorer, style) }))
    .sort((a, b) => b.score - a.score);
}

// the styles, the closest to the perfume first (the largest first for an empty perfume)
export function styleAffinities(
  index: SuggestionIndex,
  present: readonly string[],
): { style: SuggestionStyle; logLikelihood: number }[] {
  const context = knownContext(index, present);
  return index.styles
    .map((style) => ({
      style,
      logLikelihood:
        Math.log(style.perfumeCount + 1) +
        context.reduce((sum, i) => sum + Math.log(inStyle(index, style, i, index.alpha)), 0),
    }))
    .sort((a, b) => b.logLikelihood - a.logLikelihood);
}

// ingredients of the perfume often used with `candidate`, the strongest association first
export function pairedWith(
  index: SuggestionIndex,
  candidate: string,
  present: readonly string[],
  max: number = PAIRED_WITH_MAX,
): string[] {
  const id = idOf(index, candidate);
  const candidatePrior = prior(index, id);
  return knownContext(index, present)
    .map((i) => ({
      name: index.names[i],
      both: pairCountOf(index, id, i),
      probability: conditional(index, id, i, index.alpha),
    }))
    .filter((p) => p.both >= PAIRED_WITH_MIN_COUNT && p.probability > candidatePrior)
    .sort((a, b) => b.probability - a.probability)
    .slice(0, max)
    .map((p) => p.name);
}

// The usual volume of the ingredient, never more than what is left of the usual volume of its
// category (at least 1 ml): a client accepting every suggestion stays close to a 100 ml bottle.
// On the production perfumes it is almost as close to the clients' volumes as the usual volume,
// and no category ends above 125 % of its usual volume (see docs/ingredient-suggestion.md).
// The 100 ml bottle is a hard limit: with `perfumeVolume` (all categories), never more than what
// is left in it, 0 when it is full (nothing can be added).
export function suggestedVolume(
  index: SuggestionIndex,
  name: string,
  category: string,
  usage?: CategoryUsage,
  perfumeVolume?: number,
): number {
  const ingredient = index.ingredients.get(normalizeIngredientName(name));
  let volume =
    ingredient && ingredient.count > 0 && ingredient.medianVolume > 0
      ? ingredient.medianVolume
      : (index.categoryMedianVolumes.get(category) ?? DEFAULT_SUGGESTED_VOLUME);
  const budget = index.categories.get(category);
  if (usage && budget && budget.medianVolume > 0) {
    volume = Math.min(volume, Math.max(1, budget.medianVolume - usage.volume));
  }
  volume = Math.min(PERFUME_BOTTLE_VOLUME, Math.max(1, Math.round(volume)));
  return perfumeVolume === undefined
    ? volume
    : Math.min(volume, Math.floor(bottleVolumeLeft(perfumeVolume)));
}

// usual volume of the category (ml), what the perfume already uses, what is left
export function categoryBudget(
  index: SuggestionIndex,
  category: string,
  usage: CategoryUsage,
): { usual: number; used: number; left: number } | null {
  const budget = index.categories.get(category);
  if (!budget || budget.medianVolume <= 0) return null;
  return {
    usual: budget.medianVolume,
    used: usage.volume,
    left: Math.max(0, budget.medianVolume - usage.volume),
  };
}

function shuffle<T>(items: readonly T[], random: () => number): T[] {
  const shuffled = [...items];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export { shuffle as shuffleWith };

// the best `max` ingredients of `candidates` (ingredients of one category) for a perfume made of
// `present` (ingredients of all categories), in the chosen style if any. Ties are ordered at random:
// with an empty model, the proposals are not always the first ingredients in alphabetical order
export function suggestIngredients({
  index,
  present,
  candidates,
  category,
  style = null,
  usage,
  perfumeVolume,
  max = MAX_SUGGESTIONS,
  random = Math.random,
}: {
  index: SuggestionIndex;
  present: readonly PerfumeIngredient[];
  candidates: readonly PerfumeIngredient[];
  category: CategoryName;
  style?: string | null;
  usage?: CategoryUsage;
  // volume of the whole perfume: the suggested volumes never overflow the bottle
  perfumeVolume?: number;
  max?: number;
  random?: () => number;
}): IngredientSuggestion[] {
  const presentByName = new Map(present.map((i) => [normalizeIngredientName(i.name), i]));
  const candidatesByName = new Map<string, PerfumeIngredient>();
  for (const candidate of candidates) {
    const name = normalizeIngredientName(candidate.name);
    if (!presentByName.has(name)) candidatesByName.set(name, candidate);
  }
  const presentNames = [...presentByName.keys()];
  const scored = scoreCandidates(
    index,
    presentNames,
    shuffle([...candidatesByName.keys()], random),
    { style },
  ).slice(0, max);

  return scored.map(({ name }) => ({
    ingredient: candidatesByName.get(name)!,
    volume: suggestedVolume(index, name, category, usage, perfumeVolume),
    reason: suggestionReason(index, name, presentNames, presentByName, findStyle(index, style)),
  }));
}

function suggestionReason(
  index: SuggestionIndex,
  name: string,
  presentNames: readonly string[],
  presentByName: ReadonlyMap<string, PerfumeIngredient>,
  style: SuggestionStyle | null,
): SuggestionReason | null {
  const paired = pairedWith(index, name, presentNames);
  if (paired.length > 0) {
    return { kind: "pairedWith", ingredients: paired.map((n) => presentByName.get(n)!) };
  }
  const id = idOf(index, name);
  if (style && style.perfumeCount > 0 && id !== -1) {
    const share = style.counts[id] / style.perfumeCount;
    const percent = Math.round(100 * share);
    if (percent >= STYLE_REASON_MIN_PERCENT && share / prior(index, id) >= STYLE_REASON_MIN_LIFT) {
      return { kind: "style", percent };
    }
  }
  const percent =
    index.perfumeCount === 0 ? 0 : Math.round((100 * countOf(index, id)) / index.perfumeCount);
  if (index.perfumeCount >= POPULAR_MIN_PERFUMES && percent >= POPULAR_MIN_PERCENT) {
    return { kind: "popular", percent };
  }
  return null;
}

// deterministic pseudo-random numbers in [0, 1) (mulberry32)
export function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
